import React, { useState, useEffect, useMemo, useContext } from 'react';
import { FmlxButton, FmlxIcon } from 'fmlx-common-ui';
import './BottomMenu.scss';
import {
  DisplayModes,
  GetDisplayMode,
  showAlertSnackBar,
  isDropModeUrl,
  isDropWellCompare,
} from 'utils/Global';
import BottomMenuSelector from './BottomMenuSelector';
import {
  LabelDto,
  TFocusLevelImage,
  WellDetailsStates,
} from '../../business/models/CustomModels';
import { useRMDispatch } from 'redux/hooks';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import {
  setSelectOverlay,
  setWellNavigationDetails,
  switchWellImagingSettingDetails,
} from 'redux/reducers/experimentDataReducer';
import { useIsZoomLocked } from 'redux/hooks/common';
import { toggleZoomLock } from 'redux/reducers/commonReducer';
import useRMLite from 'CustomHooks/useRMLite';
import { setDrawingTool } from 'redux/reducers/wellReducer';
import { OverlayTools } from 'components/well/drawingManager/constants';
import { WellDisplayMode } from 'components/well/constants';
import WellService from 'business/services/WellService';
import useFrapDataPointsExport from 'CustomHooks/useFrapDataPointsExport';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  DropsDTO,
  InspectionSetDTO,
  ProfileDTO,
  RegionsDTO,
} from 'business/models/Models';
import {
  useFetchDrawingIdentifiers,
  useInspectionChange,
  useSetDropImageParameters,
  useUpdateCompositeImagesHandler,
} from 'components/Experiment/hooks';
import {
  useAllPlates,
  useSelectedDropImageParameters,
  useLastSelectedWellNavigationDetails,
  useAllDropImageParameters,
} from 'redux/hooks/experiment';
import {
  useFetchWellDropImageMetaData,
  useSetCurrentDropImage,
  useShowFrapGraph,
  useSelectedDrawingTool,
  useWellDropCompareMode,
  useWellDropCompareLockPan,
} from 'redux/hooks/well';
import { buildExperimentDropPath, updateDropPath } from 'utils/ExperimentUtils';
import {
  DropImageParameters,
  DropUrlParameters,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import { ExpDashboardContext } from 'components/main/ExperimentDashboardContext';
import { TraversalDirection } from 'components/well/slideShow/constants';
import { getWellNavigationDetails } from 'components/well/slideShow/utils';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { ArrowKeys, KeyboardKeys } from 'constants/';
import { useFocusLevelImages } from 'components/well/hooks';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';
import { useLocation, useNavigate } from 'react-router';

const loadStyles = (active = false, disabled = false) => {
  if (active) {
    return 'flex-xy-center bottom-menu-canvas-mode-active';
  } else if (disabled) {
    return 'flex-xy-center bottom-menu-canvas-mode-disabled';
  } else {
    return 'flex-xy-center bottom-menu-canvas-mode';
  }
};

interface BottomMenuTabProps {
  customStyle?: React.CSSProperties;
  isDisabled?: boolean;
  onClick: any;
  active?: boolean;
  Icon: any;
}

export function BottomMenuTab(props: BottomMenuTabProps) {
  const { Icon, active = false, isDisabled = false, onClick, customStyle = {} } = props;
  return (
    <div
      id="button-menu-tab-action"
      className={loadStyles(active, isDisabled)}
      {...(!isDisabled && { onClick: onClick })}
      style={customStyle}
    >
      {Icon}
    </div>
  );
}

interface Props {
  docData: ExperimentDtoClient;
  plateLables: LabelDto[];
  wellLables: LabelDto[];
  dropLables: DropsDTO[];
  regions: RegionsDTO[];
  profiles: ProfileDTO[];
  inspections: InspectionSetDTO[];
  setScoreView: (e: any) => void;
  setImageView: (e: any) => Promise<void>;
  zoomInvocker: (zoomIn: any) => void;
  fitToPlateInvoker: (plateId: number) => void;
  setSelectedInspectionId?: React.Dispatch<any>;
  isDisabled?: boolean;
  setStartWellSlideShow: React.Dispatch<React.SetStateAction<boolean>>;
  setSlideShowInitiatedFromCanvas: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function BottomMenu(props: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [plateLables, setPlateLables] = useState([]);
  const [wellLables, setWellLables] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [regions, setRegions] = useState([]);
  const [dropLables, setDropLables] = useState([]);
  const [showFrapGraph, setShowFrapGraph] = useShowFrapGraph();
  const expId = props.docData.experimentId;
  const isScreenMode = window?.location?.pathname.includes('screen') ? true : false;
  const isWellMode = window?.location?.pathname.includes('well') ? true : false;
  const isDropMode = isDropModeUrl();
  const isDropWellCompareMode = isDropWellCompare();
  const { isCompareModeOverlay } = useWellDropCompareMode();
  const dispatch = useRMDispatch();
  const fetchWellDropImageMetaData = useFetchWellDropImageMetaData();

  const isZoomLocked = useIsZoomLocked();
  const rmLite = useRMLite();
  const [, isFrap] = useFrapDataPointsExport();
  const selectedDrawingTool = useSelectedDrawingTool();
  const fetchDrawingIdentifiers = useFetchDrawingIdentifiers();
  const plates = useAllPlates();
  const updateCompositeImages = useUpdateCompositeImagesHandler();
  const expDashboardContext = useContext(ExpDashboardContext);
  const setDropImageParametersInRedux = useSetDropImageParameters(
    expDashboardContext.navigatorMap,
  );
  const onInspectionChange = useInspectionChange(
    inspections,
    expDashboardContext.navigatorMap,
  );
  const setCurrentDropImage = useSetCurrentDropImage();
  const { compareModeLockPan, toggleCompareModeLockPan } = useWellDropCompareLockPan();

  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const allDropImageParameters = useAllDropImageParameters();
  const { wellNum, plateNum, plateID } = wellNavigationDetails || {};
  const { cpId, dropNumber, inspection, regionName } = selectedDropImageParameters || {};
  const imageBatchId = inspection?.groupedInspections?.find(
    (gi) => gi.plateId === plateID,
  )?.imageBatchId;
  const wellDetails = useMemo(() => {
    return {
      wellNum,
      plateNum,
      plateID,
      cpID: cpId,
      dropNumber,
      inspectionSet: inspection,
      regionName,
    } as WellDetailsStates;
  }, [wellNavigationDetails, selectedDropImageParameters]);

  const focusLevelImages = useFocusLevelImages(
    wellNavigationDetails,
    selectedDropImageParameters,
  );
  const experimentNavigatorVisible = useSystemProperty(
    PropertyConstants.ExperimentNavigatorVisible,
    true,
  );

  const displayMode = GetDisplayMode();

  useEffect(() => {
    if (props.plateLables) {
      setPlateLables(props.plateLables);
    }
  }, [props.plateLables]);

  useEffect(() => {
    if (props.wellLables) {
      setWellLables(props.wellLables);
    }
  }, [props.wellLables]);

  useEffect(() => {
    if (props.profiles) {
      setProfiles(props.profiles);
    }
  }, [props.profiles]);

  useEffect(() => {
    if (props.inspections) {
      setInspections([...props.inspections]);
    }
  }, [props.inspections]);

  useEffect(() => {
    if (props.regions) {
      setRegions(props.regions);
    }
  }, [props.regions]);

  useEffect(() => {
    if (props.dropLables) {
      setDropLables(props.dropLables);
    }
  }, [props.dropLables]);

  useEffect(() => {
    if (!inspection) {
      return;
    }

    fetchWellDropImageMetaData({
      experimentId: expId,
      inspectionId: inspection.id,
      batchIds: inspection.groupedInspections?.map((gi) => gi.imageBatchId),
    });
  }, [inspection?.id]);

  function fullScreen() {
    if (props.zoomInvocker) {
      props.zoomInvocker(0);
    }
  }

  function zoomOut() {
    if (props.zoomInvocker) {
      props.zoomInvocker(-1);
    }
  }

  function zoomIn() {
    if (props.zoomInvocker) {
      props.zoomInvocker(+1);
    }
  }

  function fitToPlate(plateId: number) {
    const matchingPlate = plates.find((plate) => plate.plateId === plateId);

    if (props.fitToPlateInvoker) {
      props.fitToPlateInvoker(matchingPlate ? plateId : plates?.[0]?.plateId);
    }
  }

  function onPlateChange(value) {
    const plateId = parseInt(value, 10);
    updateDropPath(
      location.pathname,
      [{ index: DropUrlParameters.PLATEID, value: plateId }],
      navigate,
    );
    const plateNumber = plateLables.findIndex((pl) => pl.id === plateId) + 1;
    const navigationDetails: WellNavigationDetails = {
      ...wellNavigationDetails,
      plateID: plateId,
      plateNum: plateNumber,
    };
    dispatch(setWellNavigationDetails([navigationDetails]));
  }

  function onRegionChange(regionName, direction?: 'next' | 'previous') {
    updateDropPath(
      location.pathname,
      [{ index: DropUrlParameters.REGIONNAME, value: regionName }],
      navigate,
    );
    const wellInfo: DropImageParameters = {
      ...selectedDropImageParameters,
      regionName,
    };
    setDropImageParametersInRedux(wellInfo, 'regionName', direction);
    updateCompositeImages(wellNavigationDetails, wellInfo);
    fetchDrawingIdentifiers(plates, { ...wellDetails, regionName });
  }

  function onDropChange(dropNumber, direction?: 'next' | 'previous') {
    const _dropNumber = parseInt(dropNumber, 10);
    updateDropPath(
      location.pathname,
      [{ index: DropUrlParameters.DROPNUMBER, value: _dropNumber }],
      navigate,
    );
    const wellInfo: DropImageParameters = {
      ...selectedDropImageParameters,
      dropNumber: _dropNumber,
    };
    setDropImageParametersInRedux(wellInfo, 'dropNumber', direction);
    updateCompositeImages(wellNavigationDetails, wellInfo);
  }

  function onWellChange(value) {
    const wellNumber = parseInt(value, 10);
    updateDropPath(
      location.pathname,
      [{ index: DropUrlParameters.WELLNUM, value: wellNumber }],
      navigate,
    );
    const navigationDetails: WellNavigationDetails = {
      ...wellNavigationDetails,
      wellNum: wellNumber,
    };
    dispatch(setWellNavigationDetails([navigationDetails]));
  }

  async function onProfileChange(value, direction?: 'next' | 'previous') {
    const cpId = parseInt(value, 10);

    if (isCompareModeOverlay) {
      const cpIdExistsInOverlay = allDropImageParameters.some((x) => x.cpId === cpId);

      if (cpIdExistsInOverlay) {
        return;
      }

      switchOverlayWellsOrder();
    }

    updateDropPath(
      location.pathname,
      [{ index: DropUrlParameters.CPID, value: cpId }],
      navigate,
    );
    const wellInfo: DropImageParameters = {
      ...selectedDropImageParameters,
      cpId,
    };
    setDropImageParametersInRedux(wellInfo, 'cpId', direction);
    updateCompositeImages(wellNavigationDetails, wellInfo);
    fetchDrawingIdentifiers(plates, { ...wellDetails, cpID: cpId });
  }

  const onRegenerateComposite = async () => {
    const wellInfo: DropImageParameters = {
      ...selectedDropImageParameters,
      dropNumber,
    };
    updateCompositeImages(wellNavigationDetails, wellInfo, true);
  };

  const traverseWell = (step: number) => {
    const nextWellDetails = getWellNavigationDetails(
      props.docData?.plateDto,
      wellDetails,
      step,
      TraversalDirection.Row,
      expDashboardContext.imagedWells,
    );
    if (
      nextWellDetails.plateID ===
        expDashboardContext.imagedWells[expDashboardContext.imagedWells.length - 1]
          ?.plateId &&
      nextWellDetails.wellNum ===
        expDashboardContext.imagedWells[expDashboardContext.imagedWells.length - 1]
          ?.wellNumber
    ) {
      showAlertSnackBar(
        EAlertType.INFO,
        ESnackBarPosition.TOP,
        "You've reached the end of the experiment",
      );
    }
    updateDropPath(
      location.pathname,
      [
        { index: DropUrlParameters.WELLNUM, value: nextWellDetails.wellNum },
        { index: DropUrlParameters.PLATEID, value: nextWellDetails.plateID },
      ],
      navigate,
    );
    dispatch(setWellNavigationDetails([nextWellDetails]));
  };

  function onNextWellSelect() {
    traverseWell(1);
  }

  function onPreviousWellSelect() {
    traverseWell(-1);
  }

  const preventArrowNavigation = (e: React.KeyboardEvent<HTMLElement>) => {
    if (ArrowKeys.includes(e.key as KeyboardKeys) || e.key === KeyboardKeys.Space) {
      e.preventDefault();
    }
  };

  const getFrapGraph = async () => {
    const wellService = new WellService();
    if (!showFrapGraph && focusLevelImages?.length) {
      // in case of frap last focus level image is the 'recovery curve' image
      const lastFocusLevel = focusLevelImages[focusLevelImages.length - 1];
      setShowFrapGraph(true);
      setCurrentDropImage({
        image: lastFocusLevel.image,
        imageId: lastFocusLevel.imageId,
        imageTitle: lastFocusLevel.imageTitle,
        imageTypeId: lastFocusLevel.imageTypeId,
        imageTypeIndex: lastFocusLevel.imageTypeIndex,
        pixelSize: lastFocusLevel.pixelSize,
        imageTypeShortName: lastFocusLevel.imageTypeShortName,
      } as TFocusLevelImage);
    } else {
      setShowFrapGraph(false);
      await wellService
        .getFocusLevels(plateID, wellNum, imageBatchId, dropNumber, cpId, regionName)
        .then((data) => {
          const efImage = data?.find((fl) => fl.imageTypeId === 2);
          if (efImage) {
            setCurrentDropImage({
              imageId: efImage.imageId,
              imageTitle: efImage.imageTitle,
            } as TFocusLevelImage);
          }
        });
    }
  };

  const switchOverlayWellsOrder = () => {
    dispatch(switchWellImagingSettingDetails());
  };

  return (
    <div
      className={`flex flex-space-between bottom-menu-container h-12`}
      id={'bottommenu-main-div'}
      data-testid={'bottommenu-main-div'}
    >
      <div className="flex-y-center gap-2 ml-[6px] p-2 w-md">
        {experimentNavigatorVisible && (
          <>
            <FmlxButton
              id="best-fit-button"
              data-testid="best-fit-button"
              onClick={fullScreen}
              disabled={(isDropMode && isZoomLocked) || props.isDisabled}
              type={FmlxButton.Type.BASIC}
              size={FmlxButton.Size.MEDIUM}
              onlyIcon
              icon={<FmlxIcon name={'EnterFullscreen'} />}
              tooltipDescription="Best Fit"
              withTooltip
              onKeyDown={preventArrowNavigation}
            />
            <FmlxButton
              id="zoom-out-button"
              data-testid="zoom-out-button"
              onClick={zoomOut}
              disabled={(isDropMode && isZoomLocked) || props.isDisabled}
              type={FmlxButton.Type.BASIC}
              size={FmlxButton.Size.MEDIUM}
              onlyIcon
              icon={<FmlxIcon name={'ZoomOut'} />}
              tooltipDescription="Zoom Out"
              withTooltip
              onKeyDown={preventArrowNavigation}
            />
            <FmlxButton
              id="zoom-in-button"
              data-testid="zoom-in-button"
              onClick={zoomIn}
              disabled={(isDropMode && isZoomLocked) || props.isDisabled}
              type={FmlxButton.Type.BASIC}
              size={FmlxButton.Size.MEDIUM}
              onlyIcon
              icon={<FmlxIcon name={'ZoomIn'} />}
              tooltipDescription="Zoom In"
              withTooltip
              onKeyDown={preventArrowNavigation}
            />
            {isDropWellCompareMode && isCompareModeOverlay && (
              <>
                <FmlxButton
                  id="switch-overlay-button"
                  data-testId="switch-overlay-button"
                  onClick={switchOverlayWellsOrder}
                  disabled={props.isDisabled}
                  type={FmlxButton.Type.BASIC}
                  size={FmlxButton.Size.MEDIUM}
                  onlyIcon
                  icon={
                    <RMSvgIcon
                      name={'OverlaySwitch'}
                      fill={props.isDisabled ? '#B4B4B4' : 'white'}
                    />
                  }
                  tooltipDescription="Switch Overlay"
                  withTooltip
                  onKeyDown={preventArrowNavigation}
                />
                <FmlxButton
                  id="lock-pan-button"
                  data-testId="lock-pan-button"
                  onClick={toggleCompareModeLockPan}
                  disabled={props.isDisabled}
                  type={FmlxButton.Type.BASIC}
                  size={FmlxButton.Size.MEDIUM}
                  onlyIcon
                  icon={
                    <RMSvgIcon
                      name={compareModeLockPan ? 'OverlayPanLock' : 'OverlayPanUnlock'}
                      fill={props.isDisabled ? '#B4B4B4' : 'white'}
                    />
                  }
                  tooltipDescription={compareModeLockPan ? 'Unlock Pan' : 'Lock Pan'}
                  withTooltip
                  onKeyDown={preventArrowNavigation}
                />
              </>
            )}
            {!isDropWellCompareMode && (
              <FmlxButton
                id="pan-button"
                data-testid="pan-button"
                onClick={() => {
                  if (selectedDrawingTool === OverlayTools.PAN) {
                    dispatch(setDrawingTool(OverlayTools.POINTER));
                  } else {
                    dispatch(setDrawingTool(OverlayTools.PAN));
                  }
                }}
                type={FmlxButton.Type.BASIC}
                size={FmlxButton.Size.MEDIUM}
                onlyIcon
                icon={<FmlxIcon name="Hand" />}
                variant={
                  selectedDrawingTool === OverlayTools.PAN
                    ? FmlxButton.Variant.OUTLINE
                    : FmlxButton.Variant.CONTAIN
                }
                tooltipDescription="Pan Image"
                withTooltip
                disabled={props.isDisabled}
                onKeyDown={preventArrowNavigation}
              />
            )}
            {(displayMode === DisplayModes.IMAGE ||
              displayMode === DisplayModes.DESIGN ||
              displayMode === DisplayModes.SCORE ||
              displayMode === DisplayModes.WELLINGREDIENT) && (
              <FmlxButton
                data-testid="fit-to-plate-button"
                id="fit-to-plate-button"
                onClick={() => fitToPlate(plateID)}
                type={FmlxButton.Type.BASIC}
                size={FmlxButton.Size.MEDIUM}
                disabled={(isDropMode && isZoomLocked) || props.isDisabled}
                icon={
                  <RMSvgIcon
                    name={'FitToPlate'}
                    fill={props.isDisabled ? '#B4B4B4' : 'white'}
                  />
                }
                onlyIcon
                tooltipDescription="Fit to Plate"
                onKeyDown={preventArrowNavigation}
              />
            )}

            <>
              {isDropMode && (
                <FmlxButton
                  id="bottommenu-zoom-lock-button"
                  onClick={() => dispatch(toggleZoomLock())}
                  icon={<FmlxIcon name={isZoomLocked ? 'Unlock' : 'Lock'} />}
                  size={FmlxButton.Size.MEDIUM}
                  type={FmlxButton.Type.BASIC}
                  onlyIcon
                  tooltipDescription={isZoomLocked ? 'Unlock Zoom' : 'Lock Zoom'}
                  disabled={props.isDisabled}
                  onKeyDown={preventArrowNavigation}
                />
              )}

              <div id="bottommenu-plate-selection-parent" className="rmSelectParent">
                <BottomMenuSelector
                  id="bottommenu-plate-selection-rmselect"
                  className="labelButton"
                  valueList={plateLables}
                  onValueChange={onPlateChange}
                  selectedValue={plateID}
                  toolTip="Plate"
                  sort={false}
                  disabled={props.isDisabled}
                  onKeyDown={preventArrowNavigation}
                />
              </div>
              <div id="bottommenu-well-selection-parent" className="rmSelectParent">
                <BottomMenuSelector
                  id="bottommenu-well-selection-rmselect"
                  className="labelButton"
                  valueList={wellLables}
                  onValueChange={onWellChange}
                  onNextSelect={isDropWellCompareMode ? onNextWellSelect : undefined}
                  onPreviousSelect={
                    isDropWellCompareMode ? onPreviousWellSelect : undefined
                  }
                  selectedValue={wellNum}
                  toolTip="Well"
                  disabled={props.isDisabled}
                  onKeyDown={preventArrowNavigation}
                />
              </div>

              {!isScreenMode && !isWellMode ? (
                <div id="bottommenu-drop-selection-parent" className="rmSelectParent">
                  <BottomMenuSelector
                    id="bottommenu-drop-selection-rmselect"
                    className="labelButton"
                    valueList={dropLables}
                    onValueChange={onDropChange}
                    selectedValue={dropNumber}
                    toolTip="Drop"
                    disabled={props.isDisabled}
                    onKeyDown={preventArrowNavigation}
                  />
                </div>
              ) : null}
              {displayMode !== DisplayModes.DESIGN &&
                displayMode !== DisplayModes.WELLINGREDIENT && (
                  <>
                    <div
                      id="bottommenu-region-selection-parent"
                      className="rmSelectParent"
                    >
                      <BottomMenuSelector
                        id="bottommenu-region-selection-rmselect"
                        className="labelButton"
                        valueList={regions}
                        onValueChange={onRegionChange}
                        selectedValue={regionName}
                        toolTip="Region"
                        disabled={props.isDisabled}
                        onKeyDown={preventArrowNavigation}
                      />
                    </div>
                    <div
                      id="bottommenu-imaging-settings-selection-parent"
                      className="rmSelectParent"
                    >
                      <BottomMenuSelector
                        id="bottommenu-imaging-settings-selection-rmselect"
                        className="labelButton"
                        valueList={profiles}
                        onValueChange={onProfileChange}
                        selectedValue={cpId}
                        toolTip="Imaging Settings"
                        disabled={props.isDisabled}
                        onKeyDown={preventArrowNavigation}
                      />
                    </div>
                    <div
                      id="bottommenu-inspection-selection-parent"
                      className="rmSelectParent"
                    >
                      <BottomMenuSelector
                        id="bottommenu-inspection-selection-rmselect"
                        className="labelButton"
                        valueList={props.inspections}
                        onValueChange={onInspectionChange}
                        selectedValue={inspection?.id}
                        toolTip="Inspections"
                        disabled={props.isDisabled}
                        onKeyDown={preventArrowNavigation}
                      />
                    </div>
                  </>
                )}
            </>
          </>
        )}

        <>
          {(displayMode === DisplayModes.WELLIMAGE ||
            displayMode === DisplayModes.IMAGE) &&
            !rmLite && (
              <FmlxButton
                id="bottommenu-slideshow-button"
                type={FmlxButton.Type.BASIC}
                icon={<FmlxIcon name={'RockMakerSlideShow'} />}
                size={FmlxButton.Size.MEDIUM}
                onlyIcon={true}
                tooltipDescription="Slideshow"
                onClick={() => {
                  if (displayMode !== DisplayModes.WELLIMAGE) {
                    props.setSlideShowInitiatedFromCanvas(true);
                    navigate(
                      buildExperimentDropPath({
                        expId,
                        plateID,
                        wellNum,
                        dropNumber,
                        cpId,
                        regionName,
                        imageBatchId,
                        wellMode: WellDisplayMode.Drop,
                      }),
                    );
                  } else {
                    props.setSlideShowInitiatedFromCanvas(false);
                  }
                  props.setStartWellSlideShow(true);
                  dispatch(setSelectOverlay(null));
                  document.documentElement.requestFullscreen();
                }}
                disabled={props.isDisabled}
                onKeyDown={preventArrowNavigation}
              />
            )}
          {isDropMode && isFrap && (
            <button
              className={`uppercase rounded-md ${
                showFrapGraph ? 'bg-black text-white' : 'bg-transparent'
              }`}
              onClick={getFrapGraph}
            >
              Frap Data Point
            </button>
          )}
        </>
        {displayMode === DisplayModes.IMAGE && (
          <FmlxButton
            id="bottommenu-regenerate-composite-button"
            onClick={onRegenerateComposite}
            size={FmlxButton.Size.MEDIUM}
            type={FmlxButton.Type.BASIC}
            icon={
              <RMSvgIcon
                name={'RefreshImage'}
                fill={props.isDisabled ? '#B4B4B4' : 'white'}
              />
            }
            onlyIcon={true}
            tooltipDescription="Regenerate Composite Images"
            disabled={props.isDisabled}
            onKeyDown={preventArrowNavigation}
          />
        )}
      </div>
    </div>
  );
}
